import { useState, useEffect, useMemo } from "react";
import { createAssistant, getAssistants, removeAssistant } from "./service";
import { useOutletContext, useNavigate } from "react-router-dom";
import { AssistantsTable, Icons } from "./AssistantsTable";
import { AssistantCreate } from "./AssistantCreate";
import { TVoiceAssistant } from "./mock/voiceAssistant";
import { TSMSAssistant } from "./mock/smsAssistant";
import { TWhatsappAssistant } from "./mock/whatsappAssistant";
import { TEmailAssistant } from "./mock/emailAssistant";
import { TEmbedAssistant } from "./mock/embedAssistant";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { Button } from "V2.0/common/button";
import { message } from "antd";
import { AssistantDelete } from "./AssistantDelete";
import { AssistantDuplicate } from "./AssistantDuplicate";
import { AssistantRename } from "./AssistantRename";

import { Heading } from "V2.0/common/heading";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { CustomerViewModel } from "V2.0/hooks/useCustomer";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { UserAccessLevel } from "V2.0/models/UserProfile";

const assistantTypes = ["Voice", "SMS", "WhatsApp", "Messenger", "Instagram"];

type Assistant =
  | TVoiceAssistant
  | TSMSAssistant
  | TWhatsappAssistant
  | TEmailAssistant
  | TEmbedAssistant;

function useAssistants(projectId: string) {
  const [assistants, setAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAssistants = async () => {
    try {
      const response = await getAssistants({ projectId });
      if (response.status === 200) {
        setIsLoading(false);
        setAssistants(response.data.assistants);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error getting assistants");
    }
  };

  return {
    assistants,
    isLoading,
    fetchAssistants,
  };
}

function useAssistantsWithLoading(projectId: string) {
  const [assistants, setAssistants] = useState({
    data: [],
    isLoading: true,
  });

  const fetchAssistants = async () => {
    try {
      const response = await getAssistants({ projectId });
      if (response.status === 200) {
        setAssistants({
          data: response.data.assistants,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error("Error getting assistants");
      message.error(
        error.response.data?.message ||
          error.response.data?.msg ||
          error.response.data?.error ||
          "Failed to get assistants",
      );
      setAssistants((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
    }
  };
}

export function Assistants() {
  const {
    projectId,
    company,
    user,
    assistants: data,
    isAssistantsLoading: isLoading,
    fetchAssistants,
    isViewOnly,
  } = useOutletContext<{
    projectId: string;
    company: any;
    user: CustomerViewModel;
    assistants: any;
    isAssistantsLoading: boolean;
    fetchAssistants: (projectId: string, loading?: boolean) => VoidFunction;
    isViewOnly: boolean;
  }>();

  const navigate = useNavigate();

  const [openDelete, setOpenDelete] = useState({
    item: null,
    isOpen: false,
  });
  const [openDuplicate, setOpenDuplicate] = useState({
    item: null,
    isOpen: false,
  });
  const [openRename, setOpenRename] = useState({
    item: null,
    isOpen: false,
  });

  useEffect(() => {
    if (projectId) {
      fetchAssistants(projectId);
    }
  }, [projectId]);

  const assistants = useMemo(() => {
    if (isLoading) {
      return { data: [], isLoading: isLoading };
    }

    const _assistants = data.filter((assistant) => !assistant?.hidden);

    return {
      data: _assistants,
      isLoading: isLoading,
    };
  }, [data, isLoading]);

  const onClose = () => setOpenDelete({ item: null, isOpen: false });
  const onDuplicateClose = () =>
    setOpenDuplicate({ item: null, isOpen: false });
  const onRenameClose = () => setOpenRename({ isOpen: false, item: null });

  const handleDeleteAssistant = async () => {
    message.loading("Deleting Assistant");
    try {
      const response = await removeAssistant({
        workspaceId: projectId,
        assistantId: openDelete.item._id,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Assistant deleted");
        fetchAssistants(projectId, false);
        onClose();
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to delete assistant",
      );
      console.error("Error deleting assistant");
    }
  };

  const onDuplicationSuccess = () => {
    fetchAssistants(projectId, false);
  };

  const showAssistantCreate =
    (user?.isOwner ||
      user.userAccessLevel === UserAccessLevel.WHITELABELOPERATOR) &&
    !isViewOnly;
    
  const allowAssistantCreation =
    user?.userAccessLevel === "OWNER" ||
    assistants.data.length + 1 <= user?.whitelabelOwner?.assistantsPerWorkspace;

  const isAssistantsEmpty =
    !assistants.isLoading && assistants.data.length === 0;

  return (
    <>
      <div className="max-w-7xl h-full px-16 mx-auto py-4">
        <div className="w-full h-full flex flex-col gap-y-8">
          <div className="flex-shrink-0 flex justify-between p-4 items-center">
            <div className="flex gap-x-2 items-center">
              <Heading>Assistants</Heading>
            </div>
            {showAssistantCreate && (
              <AssistantCreate
                projectId={projectId}
                allowAssistantCreation={allowAssistantCreation}
                onAssistantCreationSuccess={() =>
                  fetchAssistants(projectId, false)
                }
              />
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {assistants.isLoading ? (
              <div className="h-96 w-full border rounded-lg">
                <div className="h-full w-full flex justify-center items-center">
                  <div className="flex flex-col gap-y-4 items-center">
                    <AiOutlineLoading3Quarters className="size-6 animate-spin" />
                  </div>
                </div>
              </div>
            ) : isAssistantsEmpty ? (
              <div className="h-96 w-full border border-gray-200 rounded-lg">
                <div className="h-full w-full flex justify-center items-center">
                  <div className="flex flex-col gap-y-4 items-center">
                    <WrenchScrewdriverIcon className="size-10 text-gray-500" />
                    <div className="flex flex-col items-center gap-y-2">
                      <p className="text-base font-semibold text-gray-900">
                        Assistants
                      </p>
                      <div className="w-80 text-sm text-gray-700">
                        Assistants handle your inbound and outbound
                        communication across the following channels: Voice, SMS,
                        WhatsApp, Messenger, and Instagram.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <AssistantsTable
                assistants={assistants}
                isViewOnly={isViewOnly}
                allowAssistantCreation={allowAssistantCreation}
                onDelete={(item) => setOpenDelete({ item, isOpen: true })}
                onDuplicate={(item) => setOpenDuplicate({ item, isOpen: true })}
                onEdit={(item) => setOpenRename({ item, isOpen: true })}
              />
            )}
          </div>
        </div>
      </div>
      {openDelete.isOpen && (
        <AssistantDelete
          isOpen={true}
          onClose={() => setOpenDelete({ isOpen: false, item: null })}
          onDelete={handleDeleteAssistant}
          assistant={openDelete.item}
        />
      )}
      {openDuplicate.isOpen && (
        <AssistantDuplicate
          isOpen={true}
          onClose={onDuplicateClose}
          workspaceId={projectId}
          assistantName={openDuplicate.item?.name}
          assistantId={openDuplicate.item?._id}
          onSuccess={onDuplicationSuccess}
        />
      )}
      {openRename.isOpen && (
        <AssistantRename
          isOpen={true}
          onClose={onRenameClose}
          workspaceId={projectId}
          assistantName={openRename.item?.name}
          assistantDescription={openRename.item?.description}
          assistantId={openRename.item?._id}
          onSuccess={() => fetchAssistants(projectId, false)}
        />
      )}
    </>
  );
}

export { useAssistants };
